import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from '@tanstack/react-query';
import {
  useVote,
  VoteAPIResponseCode,
} from '@telescope/cassini-hooks';
import { ACTION_TYPES } from 'utils';

type AvailablePrizes = boolean | null;

type AvailablePrizesValue = {
  hasAvailablePrize: AvailablePrizes,
  refetchAvailablePrizes: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<AvailablePrizes, Error>>;
}

export const useAvailablePrizes = ({ isEnabled, sid } : { isEnabled: boolean, sid: number }): AvailablePrizesValue => {
  const vote = useVote();

  const checkAvailablePrizes = useCallback(async (): Promise<boolean | null> => {
    if (!isEnabled) {
      return null;
    }

    const { response_code, available_seed_count } = await vote({
      action_type: ACTION_TYPES.CHECK_PRIZES
    });

    if (response_code !== VoteAPIResponseCode.VALID || !available_seed_count) {
      return null;
    }

    return parseInt(available_seed_count, 10) > 0
  }, [isEnabled])

  const {
    data: hasAvailablePrize,
    refetch,
  } = useQuery<any, Error>({
    queryKey: ['available-prizes', { sid }],
    queryFn: checkAvailablePrizes,
    enabled: false,
    staleTime: Infinity,
    suspense: false
  });

  const value = useMemo(() => ({
    hasAvailablePrize,
    refetchAvailablePrizes: refetch
  }), [
    hasAvailablePrize,
    refetch
  ])

  return value;
}